import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ChevronLeft, ChevronRight, Instagram, Pause, Play, ShoppingBag, Volume2, VolumeX } from "lucide-react";
import { api, inr } from "@/lib/api";
import Reveal, { EASE } from "@/components/Reveal";

function ReelCard({ video, isCenter, videoRef, muted, onTogglePlay, onToggleMute, playing, error }) {
  const product = video.product || null;

  return (
    <div className="relative h-full w-full overflow-hidden border border-gold/25 bg-bg2">
      {isCenter && video.video_url && !error && (
        <video
          ref={videoRef}
          src={video.video_url}
          poster={video.poster_url || undefined}
          muted={muted}
          playsInline
          loop
          className="h-full w-full object-cover"
          onError={() => error?.()}
        />
      )}

      {isCenter && video.reel_url && !error && (
        <iframe
          src={`https://www.instagram.com/reel/${video.reel_url.split("/reel/")[1]?.split("/")[0] || ""}/embed`}
          title={video.title}
          className="h-full w-full bg-white"
          frameBorder="0"
          scrolling="no"
          allowFullScreen
        />
      )}

      {(!isCenter || error) && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-b from-bg2 to-[#EFE6D2] p-6 text-center">
          {error ? <AlertTriangle size={26} className="text-gold" /> : <Instagram size={26} className="text-gold" />}
          <p className="font-display text-xl font-semibold text-ink">{video.title}</p>
          {video.poster_url ? (
            <img src={video.poster_url} alt="" className="mt-2 max-h-[55%] w-full object-contain" />
          ) : (
            !isCenter && <p className="font-cinzel text-[9px] uppercase text-ink3" style={{ letterSpacing: "0.24em" }}>Tap to preview</p>
          )}
          {isCenter && error && video.reel_url && (
            <a href={video.reel_url} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold mt-3 !px-5 !py-2.5">
              Watch on Instagram
            </a>
          )}
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#14100A]/85 to-transparent p-4 pt-12">
        <p className="font-display text-lg font-semibold leading-tight text-[#F7F4EE]">{video.title}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {product ? (
            <>
              <span className="font-mono text-[11px] text-gold2">{product.name} · {inr(product.price)}</span>
              <Link
                to={`/product/${product._id}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 border border-gold/50 bg-surface/90 px-3 py-1.5 font-cinzel text-[9px] uppercase text-gold transition-colors hover:bg-gold hover:text-[#241A08]"
                style={{ letterSpacing: "0.18em" }}
                data-testid="shop-this-look"
              >
                <ShoppingBag size={11} /> Shop This Look
              </Link>
            </>
          ) : video.reel_url ? (
            <a
              href={video.reel_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 font-cinzel text-[9px] uppercase text-gold2 hover:text-gold"
              style={{ letterSpacing: "0.18em" }}
            >
              <Instagram size={11} /> View on Instagram
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function VideoCarousel() {
  const [videos, setVideos] = useState([]);
  const [state, setState] = useState("loading");
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [error, setError] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const touchX = useRef(null);
  const [product, setProduct] = useState(null);
  const [inView, setInView] = useState(false);
  const [hoverPaused, setHoverPaused] = useState(false);

  useEffect(() => {
    api
      .get("/videos")
      .then((d) => { setVideos(d.data); setState(d.data.length ? "ok" : "empty"); })
      .catch(() => setState("error"));
  }, []);

  const current = videos[index];

  useEffect(() => {
    if (!current?.product_id) { setProduct(null); return; }
    api.get(`/products/${current.product_id}`).then((d) => setProduct(d.data)).catch(() => setProduct(null));
  }, [current?.product_id]);

  useEffect(() => {
    const io = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (!entry.isIntersecting) {
          videoRef.current?.pause();
          setPlaying(false);
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) io.observe(sectionRef.current);
    return () => io.disconnect();
  }, [videos.length]);

  // Autoplay the centered native video (muted autoplay is always allowed)
  useEffect(() => {
    const v = videoRef.current;
    setError(false);
    if (v) {
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      setPlaying(false);
    }
  }, [index]);

  // Auto-advance: each reel plays on its own for 30 seconds, then the next one slides in
  useEffect(() => {
    if (!inView || hoverPaused || videos.length < 2) return;
    const iv = setInterval(() => setIndex((i) => (i + 1) % videos.length), 30000);
    return () => clearInterval(iv);
  }, [inView, hoverPaused, videos.length, index]);

  const go = (dir) => {
    if (videos.length < 2) return;
    setIndex((i) => (i + dir + videos.length) % videos.length);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.pause();
      setPlaying(false);
    } else {
      v.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <section ref={sectionRef} className="overflow-hidden bg-bg2 border-y border-gold/15" data-testid="spotted-on-instagram">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
        <Reveal>
          <div className="text-center">
            <p className="overline-tag font-semibold">Spotted on Instagram</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-ink">
              Discover the beauty of traditional jewellery
            </h2>
          </div>
        </Reveal>

        {state === "loading" && (
          <div className="mx-auto mt-12 aspect-[9/16] w-[270px] animate-pulse bg-[#EFE6D2] border border-line" />
        )}

        {state === "empty" || state === "error" ? (
          <p className="mt-10 text-center text-sm text-ink3 font-mono">
            {state === "empty" ? "No videos yet — reels and clips added from the admin will appear here." : "Couldn't load videos right now."}
          </p>
        ) : null}

        {state === "ok" && (
          <>
            <div
              className="relative mx-auto mt-12 h-[440px] sm:h-[560px] max-w-[900px]"
              style={{ perspective: 1200 }}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              onMouseEnter={() => setHoverPaused(true)}
              onMouseLeave={() => setHoverPaused(false)}
              data-testid="video-carousel"
            >
              {videos.map((v, i) => {
                let off = i - index;
                const half = Math.ceil(videos.length / 2);
                if (off > half) off -= videos.length;
                if (off < -half) off += videos.length;
                const abs = Math.abs(off);
                if (abs > 2) return null;
                const isCenter = off === 0;
                return (
                  <motion.div
                    key={v._id}
                    className="absolute left-1/2 top-1/2 w-[250px] sm:w-[300px] cursor-pointer"
                    style={{ transformStyle: "preserve-3d" }}
                    initial={false}
                    animate={{
                      x: `calc(-50% + ${off * 52}%)`,
                      y: "-50%",
                      scale: isCenter ? 1 : 0.8,
                      rotateY: `${-off * 26}deg`,
                      opacity: isCenter ? 1 : Math.max(0.3, 0.7 - abs * 0.15),
                      zIndex: 20 - abs,
                      filter: isCenter ? "brightness(1)" : "brightness(0.55) saturate(0.9)",
                    }}
                    transition={{ duration: 0.55, ease: EASE }}
                    onClick={() => (isCenter ? null : setIndex(i))}
                    data-testid={`video-card-${i}`}
                  >
                    <div className="aspect-[9/16] w-full shadow-[0_30px_60px_-30px_rgba(28,23,18,0.5)]">
                      <ReelCard
                        video={{ ...v, product: isCenter ? product : null }}
                        isCenter={isCenter}
                        videoRef={videoRef}
                        muted={muted}
                        playing={playing}
                        error={error}
                      />
                    </div>
                    {isCenter && v.video_url && (
                      <div className="mt-3 flex justify-center gap-3">
                        <button
                          onClick={togglePlay}
                          className="h-10 w-10 grid place-items-center rounded-full border border-gold/50 bg-surface text-gold hover:bg-gold hover:text-[#241A08] transition-colors"
                          aria-label={playing ? "Pause" : "Play"}
                          data-testid="video-play-pause"
                        >
                          {playing ? <Pause size={15} /> : <Play size={15} />}
                        </button>
                        <button
                          onClick={() => setMuted(!muted)}
                          className="h-10 w-10 grid place-items-center rounded-full border border-gold/50 bg-surface text-gold hover:bg-gold hover:text-[#241A08] transition-colors"
                          aria-label={muted ? "Unmute" : "Mute"}
                          data-testid="video-mute-toggle"
                        >
                          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
                        </button>
                      </div>
                    )}
                  </motion.div>
                );
              })}

              <button
                onClick={() => go(-1)}
                className="absolute left-0 sm:left-6 top-1/2 z-30 h-11 w-11 -translate-y-1/2 grid place-items-center rounded-full border border-gold/50 bg-surface/95 text-gold shadow-md transition-all hover:bg-gold hover:text-[#241A08]"
                aria-label="Previous video"
                data-testid="carousel-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => go(1)}
                className="absolute right-0 sm:right-6 top-1/2 z-30 h-11 w-11 -translate-y-1/2 grid place-items-center rounded-full border border-gold/50 bg-surface/95 text-gold shadow-md transition-all hover:bg-gold hover:text-[#241A08]"
                aria-label="Next video"
                data-testid="carousel-next"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="mt-6 flex justify-center gap-1.5">
              {videos.map((v, i) => (
                <button
                  key={v._id}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-gold" : "w-1.5 bg-gold/30"}`}
                  aria-label={`Go to video ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
